import { useEffect, useRef, useState, useCallback } from 'react';

const TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

export function useSessionTimeout(active: boolean, onTimeout: () => void) {
  const [warningVisible, setWarningVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningVisibleRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (warningRef.current) { clearTimeout(warningRef.current); warningRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();
    warningVisibleRef.current = false;
    setWarningVisible(false);

    warningRef.current = setTimeout(() => {
      warningVisibleRef.current = true;
      setSecondsLeft(Math.round(WARNING_BEFORE_MS / 1000));
      setWarningVisible(true);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => Math.max(0, s - 1));
      }, 1000);
    }, TIMEOUT_MS - WARNING_BEFORE_MS);

    timeoutRef.current = setTimeout(() => {
      clearTimers();
      warningVisibleRef.current = false;
      setWarningVisible(false);
      onTimeoutRef.current();
    }, TIMEOUT_MS);
  }, [clearTimers]);

  useEffect(() => {
    if (!active) {
      clearTimers();
      warningVisibleRef.current = false;
      setWarningVisible(false);
      return;
    }

    startTimers();

    const handleActivity = () => {
      if (!warningVisibleRef.current) {
        startTimers();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [active, startTimers, clearTimers]);

  return { warningVisible, secondsLeft, staySignedIn: startTimers };
}
